// @flow
import { createAction } from 'redux-actions';
import * as types from '../constants/Api';
import { requestCreateAccident, requestUpdateAccident, requestPrepareAccident } from '../utilities/api/accident';
import { requestCreateCar, requestUpdateCar, requestPrepareCar, requestVehicleModelsByRegistrationNumber } from '../utilities/api/car';
import { requestCreateContent, requestUpdateContent, requestPrepareContent } from '../utilities/api/content';
import {
  requestCreateCustomer,
  requestUpdateCustomer,
  requestCustomerQuote,
  requestUpdateQuote,
  requestGetActivationDate,
  requestPurchase,
  requestSaveOffer,
  requestRetrieveOffer,
  requestPortraitLead,
} from '../utilities/api/customer';
import { requestCreateHouse, requestUpdateHouse, requestPrepareHouse } from '../utilities/api/house';
import { requestCreateTravel, requestUpdateTravel, requestPrepareTravel } from '../utilities/api/travel';
import { requestCreateInsuranceCheck, requestSendRecommendation } from '../utilities/api/insuranceCheck';
import { appErrorAction } from './App';
import { requestCreatePersonal, requestPreparePersonal, requestUpdatePersonal } from '../utilities/api/personal';
import { getPolicyID } from '../utilities/api/common';
import { requestCreateDog, requestUpdateDog, requestPrepareDog } from '../utilities/api/dog';
import { requestCreateCat, requestUpdateCat, requestPrepareCat } from '../utilities/api/cat';
import { validateCoverageCode } from '../utilities/package/packageUtils';

/*
----------
General actions
----------
 */
export const apiLoading = createAction(types.API_LOADING);
export const apiLoadingAction = payload => dispatch => dispatch(apiLoading(payload));

export const apiLoaded = createAction(types.API_LOADED);
export const apiLoadedAction = payload => dispatch => dispatch(apiLoaded(payload));

export const servicesLoading = createAction(types.SERVICES_LOADING);
export const servicesLoadingAction = payload => dispatch => dispatch(servicesLoading(payload));

export const servicesLoaded = createAction(types.SERVICES_LOADED);
export const servicesLoadedAction = payload => dispatch => dispatch(servicesLoaded(payload));

export const oauthReceive = createAction(types.OAUTH_RECEIVE);
export const oauthReceiveAction = () => (dispatch) => dispatch(oauthReceive());

export const oauthRequest = createAction(types.OAUTH_REQUEST);
export const oauthRequestAction = () => (dispatch) =>
  dispatch(oauthRequest()).then(() => {
    dispatch(oauthReceiveAction());
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

/*
----------
Customer actions
----------
 */
export const createdCustomer = createAction(types.CUSTOMER_CREATED);
export const createdCustomerAction = payload => dispatch => dispatch(createdCustomer(payload));

export const createCustomer = createAction(types.CUSTOMER_CREATE);
export const createCustomerAction = (params, formValues) => dispatch =>
  dispatch(requestCreateCustomer(params, formValues)).then((data) => {
    return dispatch(createdCustomerAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const updatedCustomer = createAction(types.CUSTOMER_UPDATED);
export const updatedCustomerAction = payload => dispatch => dispatch(updatedCustomer(payload));

export const updateCustomer = createAction(types.CUSTOMER_UPDATE);
export const updateCustomerAction = (params, formValues) => dispatch =>
  dispatch(requestUpdateCustomer(params, formValues)).then((data) => {
    return dispatch(updatedCustomerAction(data));
  }).catch(data => {
    const { response: { status } } = data;
    // If we get a 422 back with an error code of 1108 we call create customer.
    if (status === 422) {
      if (typeof data === 'object') {
        const { response: { data: { errors: [error] } } } = data;
        if (error.code === 1108) {
          return dispatch(createCustomerAction(params, formValues));
        }
      }
    }
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const activationDateReceived = createAction(types.CUSTOMER_RECEIVED_ACTIVATION_DATE);
export const activationDateReceivedAction = payload => dispatch =>
  dispatch(activationDateReceived(payload));

export const activationDateGet = createAction(types.CUSTOMER_GET_ACTIVATION_DATE);
export const activationDateGetAction = (formValues, onlyMinData) => dispatch =>
  dispatch(requestGetActivationDate(onlyMinData)).then((data) => {
    if (formValues && formValues.setFieldValue) {
      Object.keys(data).forEach(key => {
        formValues.setFieldValue(key, data[key]);
      });
    }
    return dispatch(activationDateReceivedAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const quotedCustomer = createAction(types.CUSTOMER_QUOTED);
export const quotedCustomerAction = payload => dispatch => dispatch(quotedCustomer(payload));

export const quoteCustomer = createAction(types.CUSTOMER_QUOTE);
export const quoteCustomerAction = () => dispatch =>
  dispatch(requestCustomerQuote()).then((data) => {
    return dispatch(quotedCustomerAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const updatedQuote = createAction(types.UPDATED_QUOTE);
export const updatedQuoteAction = payload => dispatch => dispatch(updatedQuote(payload));

export const updateQuote = createAction(types.UPDATE_QUOTE);
export const updateQuoteAction = (params, product, formValues) => (dispatch, getState) =>
  dispatch(requestUpdateQuote(params, product, formValues)).then((data) => {
    const result = dispatch(updatedQuote(data));
    const state = getState();
    if (typeof formValues.setValues === 'function') {
      const { userData } = state;
      // Set values for the updated values and price.
      // We use setValues to update the form values back in react.
      formValues.setValues({
        ...formValues,
        ...userData,
      });
    }
    return result;
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const createUpdateCustomerAction = (params, formValues) => (dispatch, getState) => {
  const state = getState();
  // If the cpr has changed then create a new customer.
  if (state.userData.user && state.userData.user.customerID &&
    state.userData.user.cpr === formValues.cpr) {
    const { userData: { user: { customerID } } } = state;
    return dispatch(updateCustomerAction(params, {
      ...formValues,
      customerID,
    }));
  }
  return dispatch(createCustomerAction(params, formValues));
};

/*
----------
Accident actions.
----------
 */
export const createdAccident = createAction(types.ACCIDENT_CREATED);
export const createdAccidentAction = payload => dispatch => dispatch(createdAccident(payload));

export const createAccident = createAction(types.ACCIDENT_CREATE);
export const createAccidentAction = (params, formValues) => dispatch =>
  dispatch(requestCreateAccident(params, formValues)).then((data) => {
    dispatch(createdAccidentAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const updatedAccident = createAction(types.ACCIDENT_UPDATED);
export const updatedAccidentAction = payload => dispatch => dispatch(updatedAccident(payload));

export const updateAccident = createAction(types.ACCIDENT_UPDATE);
export const updateAccidentAction = (params, formValues) => dispatch =>
  dispatch(requestUpdateAccident(params, formValues)).then((data) => {
    return dispatch(updatedAccidentAction(data));
  }).catch(data => {
    const { response: { status } } = data;
    // If we get a 422 back with an error code of 1108 we call create customer.
    if (status === 422) {
      if (typeof data === 'object') {
        const { response: { data: { errors: [error] } } } = data;
        if (error && error.code === 1108) {
          return dispatch(createAccidentAction(params, formValues));
        }
      }
    }
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const preparedAccident = createAction(types.ACCIDENT_PREPARED);
export const preparedAccidentAction = payload => dispatch => dispatch(preparedAccident(payload));

export const prepareAccident = createAction(types.ACCIDENT_PREPARE);
export const prepareAccidentAction = (params, values) => dispatch =>
  dispatch(requestPrepareAccident(params, values)).then((data) => {
    dispatch(preparedAccidentAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const createUpdateAccidentAction = (params, formValues) => (dispatch, getState) => {
  const state = getState();
  const policyID = getPolicyID(state.userData, 'accident');
  if (state.userData.accident && policyID) {
    return dispatch(updateAccidentAction(params, {
      ...formValues,
      policyID,
    }));
  }
  return dispatch(createAccidentAction(params, formValues));
};

/*
----------
  Personal actions.
----------
*/
export const createdPersonal = createAction(types.PERSONAL_CREATED);
export const createdPersonalAction = payload => dispatch => dispatch(createdPersonal(payload));

export const createPersonal = createAction(types.PERSONAL_CREATE);
export const createPersonalAction = (params, formValues) => dispatch =>
  dispatch(requestCreatePersonal(params, formValues)).then((data) => {
    dispatch(createdPersonalAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const updatedPersonal = createAction(types.PERSONAL_UPDATED);
export const updatedPersonalAction = payload => dispatch => dispatch(updatedPersonal(payload));

export const updatePersonal = createAction(types.PERSONAL_UPDATE);
export const updatePersonalAction = (params, formValues) => dispatch =>
  dispatch(requestUpdatePersonal(params, formValues)).then((data) => {
    return dispatch(updatedPersonalAction(data));
  }).catch(data => {
    const { response: { status } } = data;
    // If we get a 422 back with an error code of 1108 we call create customer.
    if (status === 422) {
      if (typeof data === 'object') {
        const { response: { data: { errors } } } = data;
        const [error] = errors;
        const { code } = error;
        if (code === 1108) {
          return dispatch(createPersonalAction(params, formValues));
        }
      }
    }
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const preparedPersonal = createAction(types.PERSONAL_PREPARED);
export const preparedPersonalAction = payload => dispatch => dispatch(preparedPersonal(payload));

export const preparePersonal = createAction(types.PERSONAL_PREPARE);
export const preparePersonalAction = (params, values) => dispatch =>
  dispatch(requestPreparePersonal(params, values)).then((data) => {
    dispatch(preparedPersonalAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const createUpdatePersonalAction = (params, formValues) => (dispatch, getState) => {
  const state = getState();
  const policyID = getPolicyID(state.userData, 'personal');
  if (state.userData.personal && policyID) {
    return dispatch(updatePersonalAction(params, {
      ...formValues,
      policyID,
    }));
  }
  return dispatch(createPersonalAction(params, formValues));
};

/*
----------
Address actions.
----------
 */
export const getAddressAutosuggest = createAction(types.GET_ADDRESS_AUTOSUGGEST);
export const getAddressAutosuggestAction = payload => dispatch =>
  dispatch(getAddressAutosuggest(payload));

/*
----------
Car actions.
----------
 */
export const validatedCarRegistrationNumber = createAction(types.CAR_REGISTRATION_NUMBER_VALIDATED);
export const validatedCarRegistrationNumberAction = payload => dispatch =>
  dispatch(validatedCarRegistrationNumber(payload));

export const validateCarRegistrationNumber = createAction(types.CAR_REGISTRATION_NUMBER_VALID);
export const validateCarRegistrationNumberAction = (params, formValues) => dispatch =>
  dispatch(requestVehicleModelsByRegistrationNumber(params)).then((data) => {
    dispatch(validatedCarRegistrationNumberAction(data));
    if (typeof formValues.setValues === 'function') {
      // We use setValues to update the form values back in react.
      formValues.setValues({ ...formValues, ...data });
    }
  }).catch(() => {
    // Save state for service error.
    dispatch(validatedCarRegistrationNumberAction(
      { registrationNumberValid: null, registrationNumberNoService: true }
    ));
    if (typeof formValues.setValues === 'function') {
      // We use setValues to update the form values back in react.
      formValues.setValues({
        ...formValues,
        registrationNumberValid: null,
        registrationNumberNoService: true,
      });
    }
  });

export const createdCar = createAction(types.CAR_CREATED);
export const createdCarAction = payload => dispatch => dispatch(createdCar(payload));

export const createCar = createAction(types.CAR_CREATE);
export const createCarAction = (params, formValues) => dispatch =>
  dispatch(requestCreateCar(params, formValues)).then((data) => {
    dispatch(createdCarAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const updatedCar = createAction(types.CAR_UPDATED);
export const updatedCarAction = payload => dispatch => dispatch(updatedCar(payload));

export const updateCar = createAction(types.CAR_UPDATE);
export const updateCarAction = (params, formValues) => dispatch =>
  dispatch(requestUpdateCar(params, formValues)).then((data) => {
    return dispatch(updatedCarAction(data));
  }).catch(data => {
    const { response: { status } } = data;
    // If we get a 422 back with an error code of 1108 we call create customer.
    if (status === 422) {
      if (typeof data === 'object') {
        const { response: { data: { errors } } } = data;
        const [error] = errors;
        const { code } = error;
        if (code === 1108) {
          return dispatch(createCarAction(params, formValues));
        }
      }
    }
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const preparedCar = createAction(types.CAR_PREPARED);
export const preparedCarAction = payload => dispatch => dispatch(preparedCar(payload));

export const prepareCar = createAction(types.CAR_PREPARE);
export const prepareCarAction = (params, values) => dispatch =>
  dispatch(requestPrepareCar(params, values)).then((data) => {
    dispatch(preparedCarAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const createUpdateCarAction = (params, formValues) => (dispatch, getState) => {
  const state = getState();
  const policyID = getPolicyID(state.userData, 'car');
  if (state.userData.car && policyID) {
    return dispatch(updateCarAction(params, {
      ...formValues,
      policyID,
    }));
  }
  return dispatch(createCarAction(params, formValues));
};

/*
----------
Content actions.
----------
 */
export const createdContent = createAction(types.CONTENT_CREATED);
export const createdContentAction = payload => dispatch => dispatch(createdContent(payload));

export const createContent = createAction(types.CONTENT_CREATE);
export const createContentAction = (params, formValues) => dispatch =>
  dispatch(requestCreateContent(params, formValues)).then((data) => {
    dispatch(createdContentAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const updatedContent = createAction(types.CONTENT_UPDATED);
export const updatedContentAction = payload => dispatch => dispatch(updatedContent(payload));

export const updateContent = createAction(types.CONTENT_UPDATE);
export const updateContentAction = (params, formValues) => dispatch =>
  dispatch(requestUpdateContent(params, formValues)).then((data) => {
    return dispatch(updatedContentAction(data));
  }).catch(data => {
    const { response: { status } } = data;
    // If we get a 422 back with an error code of 1108 we call create customer.
    if (status === 422) {
      if (typeof data === 'object') {
        const { response: { data: { errors } } } = data;
        const [error] = errors;
        const { code } = error;
        if (code === 1108) {
          return dispatch(createContentAction(params, formValues));
        }
      }
    }
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const preparedContent = createAction(types.CONTENT_PREPARED);
export const preparedContentAction = payload => dispatch => dispatch(preparedContent(payload));

export const prepareContent = createAction(types.CONTENT_PREPARE);
export const prepareContentAction = (params, values) => dispatch =>
  dispatch(requestPrepareContent(params, values)).then((data) => {
    dispatch(preparedContentAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const createUpdateContentAction = (params, formValues) => (dispatch, getState) => {
  const state = getState();
  const policyID = getPolicyID(state.userData, 'content');
  if (state.userData.content && policyID) {
    return dispatch(updateContentAction(params, {
      ...formValues,
      policyID,
    }));
  }
  return dispatch(createContentAction(params, formValues));
};

/*
----------
House actions.
----------
 */
export const createdHouse = createAction(types.HOUSE_CREATED);
export const createdHouseAction = payload => dispatch => dispatch(createdHouse(payload));

export const createHouse = createAction(types.HOUSE_CREATE);
export const createHouseAction = (params, formValues) => dispatch =>
  dispatch(requestCreateHouse(params, formValues)).then((data) => {
    dispatch(createdHouseAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const updatedHouse = createAction(types.HOUSE_UPDATED);
export const updatedHouseAction = payload => dispatch => dispatch(updatedHouse(payload));

export const updateHouse = createAction(types.HOUSE_UPDATE);
export const updateHouseAction = (params, formValues) => dispatch =>
  dispatch(requestUpdateHouse(params, formValues)).then((data) => {
    return dispatch(updatedHouseAction(data));
  }).catch(data => {
    const { response: { status } } = data;
    // If we get a 422 back with an error code of 1108 we call create customer.
    if (status === 422) {
      if (typeof data === 'object') {
        const { response: { data: { errors } } } = data;
        const [error] = errors;
        const { code } = error;
        if (code === 1108) {
          return dispatch(createHouseAction(params, formValues));
        }
      }
    }
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const preparedHouse = createAction(types.HOUSE_PREPARED);
export const preparedHouseAction = payload => dispatch => dispatch(preparedHouse(payload));

export const prepareHouse = createAction(types.HOUSE_PREPARE);
export const prepareHouseAction = (params, values) => dispatch =>
  dispatch(requestPrepareHouse(params, values)).then((data) => {
    dispatch(preparedHouseAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const createUpdateHouseAction = (params, formValues) => (dispatch, getState) => {
  const state = getState();
  const policyID = getPolicyID(state.userData, 'house');
  if (state.userData.house && policyID) {
    return dispatch(updateHouseAction(params, {
      ...formValues,
      policyID,
    }));
  }
  return dispatch(createHouseAction(params, formValues));
};

/*
----------
Purchase actions.
----------
 */
export const purchased = createAction(types.PURCHASED);
export const purchasedAction = payload => dispatch =>
  dispatch(purchased(payload));

export const purchase = createAction(types.PURCHASE);
export const purchaseAction = (params, formValues) => (dispatch, getState) =>
  dispatch(requestPurchase(params, formValues)).then((data) => {
    return dispatch(purchasedAction({ ...data, isPartialPurchase: false }));
  }).catch(data => {
    if (data.response) {
      const { response, response: { status } } = data;
      if (status === 400) {
        if (typeof response.data === 'object') {
          const { data: { errors: [error] } } = response;
          if (error.code === 1120) {
            const { section: { packageData: { translations } } } = getState();
            // This means partial purchase complete. Save Partial Purchase State.
            const products = error.detail.map((product) => {
              return translations[product];
            });
            let productsText = products[0];
            if (products.length > 1) {
              productsText = products.join(translations.and);
            }
            const newData = {
              ...data,
              isPartialPurchase: true,
              partialPurchaseProductsText: productsText,
            };
            return dispatch(purchasedAction(newData));
          }
        }
      }
    }
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

/*
----------
Quote/Offer actions.
----------
 */
export const savedOffer = createAction(types.SAVED_OFFER);
export const savedOfferAction = payload => dispatch =>
  dispatch(savedOffer(payload));

export const saveOffer = createAction(types.SAVE_OFFER);
export const saveOfferAction = (params, formValues) => (dispatch) =>
  dispatch(requestSaveOffer(params, formValues)).then((data) => {
    return dispatch(savedOfferAction({ ...data }));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const portraitLeadCreated = createAction(types.PORTRAIT_LEAD_CREATED);
export const portraitLeadCreatedAction = payload => dispatch =>
  dispatch(portraitLeadCreated(payload));

export const portraitLead = createAction(types.PORTRAIT_LEAD);
export const portratiLeadAction = (params, formValues) => (dispatch) =>
  dispatch(requestPortraitLead(params, formValues)).then((data) => {
    return dispatch(portraitLeadCreatedAction({ ...data }));
  }).catch(data => {
    return Promise.resolve(data);
  });

export const retrievedOffer = createAction(types.RETRIEVED_OFFER);
export const retrievedOfferAction = payload => dispatch =>
  dispatch(retrievedOffer(payload));

export const retrieveOffer = createAction(types.RETRIEVE_OFFER);
export const retrieveOfferAction = (params, formValues) => (dispatch, getState) =>
  dispatch(requestRetrieveOffer(params, formValues)).then((dataOrg) => {
    const {
      userData, section: {
        packageData: {
          initialValues: {
            vouchers, basicCoverages, coverageList,
          },
        },
      },
    } = getState();
    const data = dataOrg;
    const product = Object.keys(data.productData).reduce((res, elem) => {
      const { coverageCodes } = data.productData[elem];
      if (basicCoverages && basicCoverages[elem]) {
        const addBasicCoverages = basicCoverages[elem].filter(
          code => validateCoverageCode(coverageList[code], {}, userData)
        );
        data.productData[elem].coverageCodes = [...coverageCodes, ...addBasicCoverages];
      }
      return elem;
    }, {});

    const voucherMetaData = vouchers || [];

    const voucherData = voucherMetaData.reduce((result, voucher) => {
      const { eligible, voucherAmount, voucherDefId, productName, packageName } = voucher;
      if (product === packageName && eligible) {
        return {
          hasVoucher: true,
          voucherAmount,
          voucherDefId,
          voucherProductName: productName,
        };
      }
      return result;
    }, {
      hasVoucher: false,
      voucherAmount: 0,
      voucherDefId: null,
      voucherProductName: null,
    });

    return dispatch(retrievedOfferAction({ ...data, voucherData }));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

/*
----------
Travel actions.
----------
 */
export const createdTravel = createAction(types.TRAVEL_CREATED);
export const createdTravelAction = payload => dispatch => dispatch(createdTravel(payload));

export const createTravel = createAction(types.TRAVEL_CREATE);
export const createTravelAction = (params, formValues) => dispatch =>
  dispatch(requestCreateTravel(params, formValues)).then((data) => {
    dispatch(createdTravelAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const updatedTravel = createAction(types.TRAVEL_UPDATED);
export const updatedTravelAction = payload => dispatch => dispatch(updatedTravel(payload));

export const updateTravel = createAction(types.TRAVEL_UPDATE);
export const updateTravelAction = (params, formValues) => dispatch =>
  dispatch(requestUpdateTravel(params, formValues)).then((data) => {
    return dispatch(updatedTravelAction(data));
  }).catch(data => {
    const { response: { status } } = data;
    // If we get a 422 back with an error code of 1108 we call create customer.
    if (status === 422) {
      if (typeof data === 'object') {
        const { response: { data: { errors } } } = data;
        const [error] = errors;
        const { code } = error;
        if (code === 1108) {
          return dispatch(createTravelAction(params, formValues));
        }
      }
    }
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const preparedTravel = createAction(types.TRAVEL_PREPARED);
export const preparedTravelAction = payload => dispatch => dispatch(preparedTravel(payload));

export const prepareTravel = createAction(types.TRAVEL_PREPARE);
export const prepareTravelAction = (params, values) => dispatch =>
  dispatch(requestPrepareTravel(params, values)).then((data) => {
    dispatch(preparedTravelAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const createUpdateTravelAction = (params, formValues) => (dispatch, getState) => {
  const state = getState();
  const policyID = getPolicyID(state.userData, 'travel');
  if (state.userData.travel && policyID) {
    return dispatch(updateTravelAction(params, {
      ...formValues,
      policyID,
    }));
  }
  return dispatch(createTravelAction(params, formValues));
};

export const createdInsuranceCheck = createAction(types.INSURANCE_CHECK_CREATED);
export const createdInsuranceCheckAction = payload =>
  dispatch => dispatch(createdInsuranceCheck(payload));

export const createInsuranceCheck = createAction(types.INSURANCE_CHECK_CREATE);
export const createInsuranceCheckAction = (params, formValues) => (dispatch, getState) => {
  return dispatch(new Promise((resolve) => {
    dispatch(requestCreateInsuranceCheck(params, formValues)).then((data) => {
      setTimeout(() => {
        const result = dispatch(createdInsuranceCheckAction(data));
        const state = getState();
        if (typeof formValues.setValues === 'function') {
          const { userData } = state;
          formValues.setValues({
            ...formValues,
            ...userData,
          });
        }
        resolve(result);
      }, 8000);
    }).catch(data => {
      dispatch(appErrorAction(data));
      return Promise.reject(data);
    });
  }));
};

export const sentRecommendation = createAction(types.INSURANCE_RECOMMENDATION_SENT);
export const sentRecommendationAction = payload =>
  dispatch => dispatch(sentRecommendation(payload));

export const sendRecommendation = createAction(types.INSURANCE_RECOMMENDATION_SEND);
export const sendRecommendationAction = (params, formValues) => (dispatch, getState) => {
  return dispatch(new Promise((resolve) => {
    dispatch(requestSendRecommendation(params, formValues)).then((data) => {
      const result = dispatch(sentRecommendationAction(data));
      const state = getState();
      if (typeof formValues.setValues === 'function') {
        const { userData } = state;
        formValues.setValues({
          ...formValues,
          ...userData,
        });
      }
      // TODO: Send error tracking. Like (!result.sent && result.errorCode)
      resolve(result);
    }).catch(data => {
      dispatch(appErrorAction(data));
      return Promise.reject(data);
    });
  }));
};

/*
----------
Dog actions.
----------
 */
export const createdDog = createAction(types.DOG_CREATED);
export const createdDogAction = payload => dispatch => dispatch(createdDog(payload));

export const createDog = createAction(types.DOG_CREATE);
export const createDogAction = (params, formValues) => dispatch =>
  dispatch(requestCreateDog(params, formValues)).then((data) => {
    dispatch(createdDogAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const updatedDog = createAction(types.DOG_UPDATED);
export const updatedDogAction = payload => dispatch => dispatch(updatedDog(payload));

export const updateDog = createAction(types.DOG_UPDATE);
export const updateDogAction = (params, formValues) => dispatch =>
  dispatch(requestUpdateDog(params, formValues)).then((data) => {
    return dispatch(updatedDogAction(data));
  }).catch(data => {
    const { response: { status } } = data;
    // If we get a 422 back with an error code of 1108 we call create customer.
    if (status === 422) {
      if (typeof data === 'object') {
        const { response: { data: { errors } } } = data;
        const [error] = errors;
        const { code } = error;
        if (code === 1108) {
          return dispatch(createDogAction(params, formValues));
        }
      }
    }
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const preparedDog = createAction(types.DOG_PREPARED);
export const preparedDogAction = payload => dispatch => dispatch(preparedDog(payload));

export const prepareDog = createAction(types.DOG_PREPARE);
export const prepareDogAction = (params, values) => dispatch =>
  dispatch(requestPrepareDog(params, values)).then((data) => {
    dispatch(preparedDogAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const createUpdateDogAction = (params, formValues) => (dispatch, getState) => {
  const state = getState();
  const policyID = getPolicyID(state.userData, 'dog');
  if (state.userData.dog && policyID) {
    return dispatch(updateDogAction(params, {
      ...formValues,
      policyID,
    }));
  }
  return dispatch(createDogAction(params, formValues));
};

/*
----------
Cat actions.
----------
 */
export const createdCat = createAction(types.CAT_CREATED);
export const createdCatAction = payload => dispatch => dispatch(createdCat(payload));

export const createCat = createAction(types.CAT_CREATE);
export const createCatAction = (params, formValues) => dispatch =>
  dispatch(requestCreateCat(params, formValues)).then((data) => {
    dispatch(createdCatAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const updatedCat = createAction(types.CAT_UPDATED);
export const updatedCatAction = payload => dispatch => dispatch(updatedCat(payload));

export const updateCat = createAction(types.CAT_UPDATE);
export const updateCatAction = (params, formValues) => dispatch =>
  dispatch(requestUpdateCat(params, formValues)).then((data) => {
    return dispatch(updatedCatAction(data));
  }).catch(data => {
    const { response: { status } } = data;
    // If we get a 422 back with an error code of 1108 we call create customer.
    if (status === 422) {
      if (typeof data === 'object') {
        const { response: { data: { errors } } } = data;
        const [error] = errors;
        const { code } = error;
        if (code === 1108) {
          return dispatch(createCatAction(params, formValues));
        }
      }
    }
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const preparedCat = createAction(types.CAT_PREPARED);
export const preparedCatAction = payload => dispatch => dispatch(preparedCat(payload));

export const prepareCat = createAction(types.CAT_PREPARE);
export const prepareCatAction = (params, values) => dispatch =>
  dispatch(requestPrepareCat(params, values)).then((data) => {
    dispatch(preparedCatAction(data));
  }).catch(data => {
    dispatch(appErrorAction(data));
    return Promise.reject(data);
  });

export const createUpdateCatAction = (params, formValues) => (dispatch, getState) => {
  const state = getState();
  const policyID = getPolicyID(state.userData, 'cat');
  if (state.userData.cat && policyID) {
    return dispatch(updateCatAction(params, {
      ...formValues,
      policyID,
    }));
  }
  return dispatch(createCatAction(params, formValues));
};


// WEBPACK FOOTER //
// ./src/actions/Api.js